// #region Imports
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedFeeSchedule
} from "work/masked-fields";
import FeeScheduleCategory from "../fee-schedule-category";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import Tooltip from "@mui/material/Tooltip";
import ListItem from "@mui/material/ListItem";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useGetFeeScheduleCategoriesQuery } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import Pagination from "@mui/material/Pagination";
import Loader from "_shared/_views/loading/loader";
import FeeScheduleCategorySchema from "../fee-schedule-category-schema";
import { withoutFeeScheduleTeamMember } from "../fee-schedule-template-category";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  paddingBottom: theme.spacing(8),
}));
const ListContainer = styled(List)(({ theme }) => ({
  flex: 1,
  maxWidth: theme.spacing(50),
  minHeight: 0,
  overflowY: "auto",
}));
const ButtonsContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
}));
const PaginationControls = styled(Pagination)(({ theme }) => ({
  '&.MuiPagination-root > ul': {
    justifyContent: 'space-evenly'
  },
  flexShrink: 0,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  width: '100%'
}));
const AddSelectedButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  marginTop: theme.spacing(2),
}));
const CategoryTitle = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
// #endregion

// #region Props/Types
type FeeScheduleCategoryListProps = {
  disabledCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | MaskedFeeSchedule;
  onCategoriesSelected: (categories: FeeScheduleCategory[]) => void;
  onCancel: () => void;
  /** When browsing from a proposal's fee-schedule editor, scopes the catalog read to that proposal's grant (need-to-know). */
  proposalId?: string;
};

/**
 * Where a row comes from. A schedule's own category and a catalog category can share a name and
 * still be two rows: publishing a proposal gives its snapshot a new catalog id.
 */
type CategoryRow = {
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  source: "schedule" | "catalog";
};

export default function FeeScheduleCategoryList({
  disabledCategories,
  onCategoriesSelected,
  onCancel,
  proposalId,
}: Readonly<FeeScheduleCategoryListProps>) {
  const pageSize = 25;
  const debounceDelay = 300;

  // #region State
  const [rawSelectedIds, setRawSelectedIds] = useState<Set<string>>(new Set());
  const [selectedCategoriesById, setSelectedCategoriesById] = useState<Map<string, FeeScheduleCategory>>(new Map());
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  /** The schedule's own spelling of each normalized name, so a message can quote it as written. */
  const disabledNames = useMemo(() => {
    if (Masked.isMasked(disabledCategories)) {
      return new Map<string, string>();
    }
    return new Map(
      disabledCategories
        .map(getCategoryName)
        .filter(Boolean)
        .map((name) => [normalizeCategoryName(name), name] as const)
    );
  }, [disabledCategories]);

  const disabledIds = useMemo(() => {
    if (Masked.isMasked(disabledCategories)) return new Set<string>();
    return new Set(disabledCategories.map(getCategoryId).filter((id): id is string => Boolean(id)));
  }, [disabledCategories]);

  const selectedIds = useMemo(() => {
    return new Set([...rawSelectedIds].filter((id) => !disabledIds.has(id)));
  }, [rawSelectedIds, disabledIds]);

  const canSubmit = useMemo(
    () => selectedIds.size > 0,
    [selectedIds]
  );
  // #region Hooks
  const categoriesAPIQuery = useGetFeeScheduleCategoriesQuery({
    query: debouncedSearchTerm,
    pageIndex: page - 1,
    pageSize,
    proposalId,
  });

  const totalPages = categoriesAPIQuery.data?.headers.totalPages ?? 0;
  const isLastPage = page >= totalPages;

  // The next page's first catalog row is the upper bound of the names this page holds, so a
  // schedule row that sorts between two pages lands on exactly one of them.
  const nextPageStartQuery = useGetFeeScheduleCategoriesQuery(
    { query: debouncedSearchTerm, pageIndex: page * pageSize, pageSize: 1, proposalId },
    { skip: !categoriesAPIQuery.data || isLastPage },
  );
  const nextPageStartName = isLastPage ? undefined : nextPageStartQuery.data?.data[0]?.name;

  const availableCategories = useMemo(() => {
    return categoriesAPIQuery.data?.data
      .map((categoryJSON) => FeeScheduleCategory.fromJSON(FeeScheduleCategorySchema.validateJSON(categoryJSON)))
      ?? [];
  }, [categoriesAPIQuery.data]);

  const displayedRows = useMemo((): CategoryRow[] => {
    const catalogRows = availableCategories.map((category): CategoryRow => ({
      category,
      source: disabledIds.has(getCategoryId(category)) ? "schedule" : "catalog",
    }));
    if (Masked.isMasked(disabledCategories)) return catalogRows;

    const catalogIds = new Set(availableCategories.map(getCategoryId));
    const bounds = {
      pageStart: page === 1 ? undefined : availableCategories[0]?.name.value,
      pageEnd: availableCategories.at(-1)?.name.value,
      nextPageStart: nextPageStartName,
      isLastPage,
    };
    const scheduleRows = disabledCategories
      .filter((category) => !catalogIds.has(getCategoryId(category)))
      .filter((category) => matchesSearchTerm(getCategoryName(category), debouncedSearchTerm))
      .filter((category) => sortsOnPage(getCategoryName(category), bounds))
      .sort((a, b) => compareCategoryNames(getCategoryName(a), getCategoryName(b)))
      .map((category): CategoryRow => ({ category, source: "schedule" }));

    return mergeByName(scheduleRows, catalogRows);
  }, [availableCategories, debouncedSearchTerm, disabledCategories, disabledIds, isLastPage, nextPageStartName, page]);

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, debounceDelay);

    return () => globalThis.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (categoriesAPIQuery.isError) {
      enqueueSnackbar("Error loading fee schedule categories", { variant: "error" });
    }
  }, [categoriesAPIQuery.isError]);

  // #region Functions
  const toggleCategorySelection = useCallback((category: FeeScheduleCategory) => {
    const categoryId = category.id.toString();
    setRawSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }

      return next;
    });
    setSelectedCategoriesById((prev) => {
      const next = new Map(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.set(categoryId, category);
      }
      return next;
    });
  }, []);

  const handleCategoryClick = useCallback(
    (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => {
      if (isMaskedCategory(category)) return;
      const unmaskedCategory = category as FeeScheduleCategory;
      toggleCategorySelection(unmaskedCategory);
    },
    [toggleCategorySelection]
  );

  const handleAddSelectedClicked = useCallback(() => {
    const selectedCategories = [...selectedCategoriesById.values()].filter((category) =>
      selectedIds.has(category.id.toString()) && !disabledNames.has(normalizeCategoryName(category.name.value))
    );

    if (selectedCategories.length === 0) return;

    onCategoriesSelected(selectedCategories.map(withoutFeeScheduleTeamMember));
  }, [disabledNames, onCategoriesSelected, selectedCategoriesById, selectedIds]);
  // #endregion

  // #region Render
  if (categoriesAPIQuery.isLoading) return <Loader />;

  return (
    <MainContainer>
      <TextField
        variant="outlined"
        fullWidth
        placeholder="Search Categories"
        disabled={!displayedRows.length && !searchTerm.length}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm.length > 0 && (
              <Tooltip title="Clear search">
                <IconButton onClick={() => setSearchTerm("")} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            ),
          }
        }}
      />
      <ListContainer>
        {displayedRows.length === 0 ? (
          <ListItem disableGutters>
            <ListItemText primary="No existing fee schedule categories" />
          </ListItem>
        ) : (
          displayedRows.map(({ category, source }) => {
            const categoryName = category.name.value;
            const categoryId = category.id.toString();
            const isOnSchedule = source === "schedule";
            // A different row that shares a name with one on the schedule: not selected, just blocked.
            const takenName = isOnSchedule ? undefined : disabledNames.get(normalizeCategoryName(categoryName));
            const isRestricted = Masked.isMasked(disabledCategories);
            const isDisabled = isOnSchedule || takenName !== undefined || isRestricted;
            const isChecked = isOnSchedule
              || (takenName === undefined && (isRestricted || selectedIds.has(categoryId)));

            return (
              <ListItemButton
                key={categoryId}
                alignItems="flex-start"
                disableGutters
                disabled={isDisabled}
                onClick={() => handleCategoryClick(category)}
              >
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    checked={isChecked}
                    tabIndex={-1}
                    disableRipple
                    // Named for the category alone, as the published-rates list names its own, so a row
                    // is addressable by role whatever chip or fee text sits beside the name.
                    slotProps={{ input: { "aria-label": categoryName } }}
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ whiteSpace: "pre-line" }}
                  primary={
                    <CategoryTitle>
                      {/* Its own element, so the name reads as exactly the name and not the chip's label too. */}
                      <span>{categoryName}</span>
                      {isOnSchedule
                        ? <Chip size="small" color="primary" label="On this fee schedule" />
                        : <Chip size="small" variant="outlined" label="Your catalog" />}
                    </CategoryTitle>
                  }
                  secondary={takenName !== undefined
                    ? `This fee schedule already has a category named ${takenName}.`
                    : isRestricted
                      ? "Unavailable because this fee schedule is restricted."
                      : category.feeString}
                />
              </ListItemButton>
            );
          })
        )}
      </ListContainer>
      <PaginationControls
        count={totalPages}
        page={page}
        disabled={totalPages <= 1}
        onChange={(_event, value) => setPage(value)}
      />
      <ButtonsContainer>
        <AddSelectedButton
          color="primary"
          variant="contained"
          disabled={!canSubmit}
          startIcon={<AddIcon />}
          onClick={handleAddSelectedClicked}
        >
          Add Selected
        </AddSelectedButton>
        <Button
          color="primary"
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </ButtonsContainer>
    </MainContainer>
  );
  // #endregion
}

// #region Helper Functions
function isMaskedCategory(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
): category is FeeScheduleCategoryMaskedCurrency {
  return category instanceof FeeScheduleCategoryMaskedCurrency;
}

function getCategoryName(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.name?.value ?? "";
}

function getCategoryId(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.id?.toString() ?? "";
}

function normalizeCategoryName(name: string): string {
  return name.trim().toLowerCase();
}

/** The catalog query's own rule: a case-insensitive "contains" on the trimmed term. */
function matchesSearchTerm(name: string, searchTerm: string): boolean {
  const term = normalizeCategoryName(searchTerm);
  return !term || name.toLowerCase().includes(term);
}

/**
 * The catalog is ordered by name in the database's collation, and that decides where a page ends.
 * This is the browser's nearest match. Where the two disagree, a row near a page edge moves to the
 * neighbouring page; a single-page list never loses one.
 */
function compareCategoryNames(a: string, b: string): number {
  return a.localeCompare(b);
}

/**
 * Whether a schedule row's name falls on the current catalog page. A page holds the names from its
 * first row up to, but not including, the next page's first row. Page 1 has no lower bound and the
 * last page has no upper bound. Until the next page's first row is known, this page's last row
 * stands in, so a name that sorts between two pages shows once that answer arrives.
 */
function sortsOnPage(
  name: string,
  bounds: { pageStart?: string; pageEnd?: string; nextPageStart?: string; isLastPage: boolean },
): boolean {
  if (bounds.pageStart !== undefined && compareCategoryNames(name, bounds.pageStart) < 0) return false;
  if (bounds.isLastPage) return true;
  if (bounds.nextPageStart !== undefined) return compareCategoryNames(name, bounds.nextPageStart) < 0;
  return bounds.pageEnd !== undefined && compareCategoryNames(name, bounds.pageEnd) <= 0;
}

/**
 * Puts each schedule row before the first catalog row that does not sort ahead of it, and keeps the
 * catalog's own order. A schedule row therefore sits directly above a catalog row of the same name.
 */
function mergeByName(scheduleRows: CategoryRow[], catalogRows: CategoryRow[]): CategoryRow[] {
  const merged: CategoryRow[] = [];
  let next = 0;
  for (const catalogRow of catalogRows) {
    const catalogName = getCategoryName(catalogRow.category);
    while (next < scheduleRows.length
      && compareCategoryNames(getCategoryName(scheduleRows[next].category), catalogName) <= 0) {
      merged.push(scheduleRows[next]);
      next += 1;
    }
    merged.push(catalogRow);
  }
  return [...merged, ...scheduleRows.slice(next)];
}
